import { useEffect, useMemo, useState } from "react";

import {
  ArrowBack,
  Edit,
  Publish,
  History as HistoryIcon,
  ArrowDropDown,
  Build as BuildIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Select,
  MenuItem,
  FormControl,
  Menu,
  Alert,
  Divider,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  ListItemIcon,
} from "@mui/material";
import {
  GetSurveyResponse,
  getSurvey,
  postSurveyCondensed,
  getOrgConfigPortal,
} from "@rubi/api-generated";
import {
  getSurveyVersionDraft,
  patchSurveyVersionDraft,
  getSurveyVersion,
  listSurveyVersions,
  getSurveyCurrentVersion,
  deleteSurveyVersionDraft,
} from "@rubi/api-generated/clientRs/sdk.gen";
import type { SurveyVersionModel } from "@rubi/api-generated/clientRs/types.gen";
import { colors } from "@rubi/ui";
import { toast } from "@rubi/ui";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

import FormBuilder from "./FormBuilder";
import { LintPanel, LintSummary } from "./linter";
import type { LintResult } from "./linter";
import ConfirmCondensedModal from "./modals/ConfirmCondensedModal";
import { DeployModal } from "./modals/DeployModal";
import EditModeModal from "./modals/EditModeModal";
import { VersionHistoryModal } from "./modals/VersionHistoryModal";
import {
  replaceAddressQuestionsWithLookupNotFounds,
  formatVersionNumber,
} from "./utils";
import { VersionComparison } from "./VersionComparison";
import "./SurveyEdit.scss";

export const SurveyEdit = () => {
  const { orgId, surveyId: surveyIdFromParams } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [mode, setMode] = useState<"edit" | "view">("edit");
  const [surveyId, setSurveyId] = useState<string | null>(null);
  const [isCondensedForm, setIsCondensedForm] = useState(false);

  const [getSurveyResponse, setGetSurveyResponse] =
    useState<GetSurveyResponse | null>(null);

  const [isEditModeDialogOpen, setIsEditModeDialogOpen] = useState(false);
  const [isConfirmCondensedModalOpen, setIsConfirmCondensedModalOpen] =
    useState(false);
  const [mainSurveyJSON, setMainSurveyJSON] = useState<any>(null);
  const [condensedSurveyJSON, setCondensedSurveyJSON] = useState<any>(null);
  const [currentEditJSON, setCurrentEditJSON] = useState<any>(null);

  const [isLoading, setIsLoading] = useState(true);

  // Version control state
  const [draftVersion, setDraftVersion] = useState<SurveyVersionModel | null>(
    null,
  );
  const [activeVersion, setActiveVersion] = useState<SurveyVersionModel | null>(
    null,
  );
  const [isDeployModalOpen, setIsDeployModalOpen] = useState(false);
  const [isVersionHistoryModalOpen, setIsVersionHistoryModalOpen] =
    useState(false);
  const [comparisonMode, setComparisonMode] = useState(false);
  const [compareFromVersion, setCompareFromVersion] =
    useState<SurveyVersionModel | null>(null);
  const [compareToVersion, setCompareToVersion] =
    useState<SurveyVersionModel | null>(null);
  const [selectedVersionView, setSelectedVersionView] = useState<
    "fromVersion" | "toVersion" | "compare"
  >("compare");
  const [generateMenuAnchor, setGenerateMenuAnchor] =
    useState<null | HTMLElement>(null);
  const [isPreDeployComparison, setIsPreDeployComparison] = useState(false);
  const [supportedLanguages, setSupportedLanguages] = useState<
    string[] | undefined
  >(undefined);
  const [isDeleteDraftModalOpen, setIsDeleteDraftModalOpen] = useState(false);
  const [isDeletingDraft, setIsDeletingDraft] = useState(false);

  // Linter state
  const [lintResult, setLintResult] = useState<LintResult | null>(null);
  const [isLintPanelExpanded, setIsLintPanelExpanded] = useState(true);
  // Demo-only: component-scoped flag to hide lint UI (header summary + bottom panel).
  // Resets when SurveyEdit unmounts/remounts, including on page reload. Deploy modal lint gate is intentionally not affected.
  const [isLintHidden, setIsLintHidden] = useState(false);

  const app = location.pathname.includes("rubiintel")
    ? "rubiintel"
    : "rubireportv2";

  useEffect(() => {
    if (location.pathname.includes("edit")) {
      setMode("edit");
      if (surveyIdFromParams) {
        setSurveyId(surveyIdFromParams);
      }
    } else if (surveyIdFromParams) {
      setMode("view");
      setSurveyId(surveyIdFromParams);
    }
  }, [surveyIdFromParams, location.pathname]);

  useEffect(() => {
    if (surveyId && orgId) {
      const loadSurvey = async () => {
        try {
          const response = await getSurveyCurrentVersion({
            path: { org_id: orgId, parent_survey_id: surveyId },
          });

          const { draft_version, active_version, draft_lint_result } =
            response.data!;

          setDraftVersion(draft_version ?? null);
          setActiveVersion(active_version ?? null);

          // Set lint result from initial load (UI will decide whether to show based on mode)
          setLintResult(draft_lint_result ?? null);

          // Set configurations based on what's available
          // In edit mode, prefer draft; in view mode, use live
          const activeVersion =
            mode === "edit"
              ? (draft_version ?? active_version)
              : (active_version ?? draft_version);

          if (activeVersion) {
            setMainSurveyJSON(activeVersion.configuration);
            setCondensedSurveyJSON(activeVersion.pdf_configuration);
            setCurrentEditJSON(activeVersion.configuration);
          } else {
            console.error("No versions found for survey");
          }

          setIsLoading(false);
        } catch (error) {
          console.error("Error loading survey versions:", error);
          setIsLoading(false);
        }
      };

      loadSurvey();
    }
  }, [surveyId, orgId, mode]);

  useEffect(() => {
    const viewParam = searchParams.get("view");
    const compareParam = searchParams.get("compare");

    if (compareParam && surveyId && orgId) {
      const [fromId, toId] = compareParam.split("~");

      if (
        comparisonMode &&
        compareFromVersion &&
        compareToVersion &&
        ((fromId === "draft" && compareFromVersion === draftVersion) ||
          compareFromVersion?.id === fromId) &&
        ((toId === "draft" && compareToVersion === draftVersion) ||
          compareToVersion?.id === toId)
      ) {
        return;
      }

      const loadComparison = async () => {
        try {
          const fromPromise =
            fromId === "draft"
              ? getSurveyVersionDraft({
                  path: { org_id: orgId, parent_survey_id: surveyId },
                })
              : getSurveyVersion({
                  path: {
                    org_id: orgId,
                    parent_survey_id: surveyId,
                    version_id: fromId,
                  },
                });

          const toPromise =
            toId === "draft"
              ? getSurveyVersionDraft({
                  path: { org_id: orgId, parent_survey_id: surveyId },
                })
              : getSurveyVersion({
                  path: {
                    org_id: orgId,
                    parent_survey_id: surveyId,
                    version_id: toId,
                  },
                });

          const [fromData, toData] = await Promise.all([
            fromPromise,
            toPromise,
          ]);

          // Note: both draft and version endpoints use #[serde(flatten)] so data is at root level
          const fromVersion = fromData.data as SurveyVersionModel | undefined;
          const toVersion = toData.data as SurveyVersionModel | undefined;

          setCompareFromVersion(fromVersion ?? null);
          setCompareToVersion(toVersion ?? null);
          setComparisonMode(true);
          setSelectedVersionView("compare");
        } catch (error) {
          console.error("Error loading comparison from URL:", error);
          toast.error("Failed to load version comparison");
          setSearchParams({});
        }
      };

      loadComparison();
    } else if (viewParam && surveyId && orgId) {
      const loadVersionView = async () => {
        try {
          const versionData = await getSurveyVersion({
            path: {
              org_id: orgId,
              parent_survey_id: surveyId,
              version_id: viewParam,
            },
          });

          // Note: version endpoint uses #[serde(flatten)] so data is at root level
          if (versionData.data) {
            setCompareFromVersion(versionData.data);
            setCompareToVersion(null);
            setComparisonMode(true);
            setSelectedVersionView("fromVersion");
          }
        } catch (error) {
          console.error("Error loading version view from URL:", error);
          toast.error("Failed to load version");
          setSearchParams({});
        }
      };

      loadVersionView();
    } else if (!viewParam && !compareParam && comparisonMode) {
      setComparisonMode(false);
      setCompareFromVersion(null);
      setCompareToVersion(null);
      setSelectedVersionView("compare");
      setIsPreDeployComparison(false);

      // Exiting comparison remounts FormBuilder from currentEditJSON, which is
      // a stale load-time snapshot (autosave keeps the caches current, not it).
      // Re-seed from the caches so we don't revert to the published version and
      // let the next autosave overwrite the draft.
      setCurrentEditJSON(
        isCondensedForm ? condensedSurveyJSON : mainSurveyJSON,
      );
    }
  }, [searchParams, surveyId, orgId]);

  // Fetch supported languages from org config
  useEffect(() => {
    async function fetchOrgConfig() {
      if (!orgId) return;
      try {
        const response = await getOrgConfigPortal({
          path: { org_id: orgId },
        });
        const rawSupportedLanguages = response.data?.supported_languages;
        if (Array.isArray(rawSupportedLanguages)) {
          // Normalize case: backend may return "enCA" but we use "enCa" (camelCase)
          const normalizedLanguages = (rawSupportedLanguages as string[]).map(
            (lang) => {
              if (lang === "enCA") return "enCa";
              if (lang === "enUS") return "enUs";
              return lang;
            },
          );
          setSupportedLanguages(normalizedLanguages);
        }
      } catch (error) {
        console.error("Failed to fetch org config:", error);
        // Don't set error state, just fall back to showing all languages
      }
    }
    fetchOrgConfig();
  }, [orgId]);

  const handleSave = async (surveyJSObject) => {
    if (!surveyId || !orgId) {
      toast.error("Cannot save: Missing survey or organization ID");
      throw new Error("Missing surveyId or orgId");
    }

    if (mode !== "edit") {
      toast.error("Cannot save: Not in edit mode");
      throw new Error("Not in edit mode");
    }

    if (comparisonMode && selectedVersionView !== "compare") {
      throw new Error("Cannot save while viewing historical version");
    }

    const currentSurvey = isCondensedForm ? mainSurveyJSON : surveyJSObject;
    const currentCondensedSurvey = isCondensedForm
      ? surveyJSObject
      : condensedSurveyJSON;

    try {
      const response = await patchSurveyVersionDraft({
        path: { org_id: orgId, parent_survey_id: surveyId },
        body: {
          configuration: currentSurvey,
          pdf_configuration: currentCondensedSurvey,
        },
      });

      if (response.data?.draft) {
        setDraftVersion(response.data.draft);
        setMainSurveyJSON(currentSurvey);
        setCondensedSurveyJSON(currentCondensedSurvey);

        // Update lint result from backend response (clear if null to remove old errors)
        setLintResult(response.data.lint_result ?? null);
      } else {
        toast.error("Failed to save draft: No data returned");
        throw new Error("No data returned from save");
      }
    } catch (error) {
      console.error("Error saving draft: ", error);
      toast.error("Failed to save draft");
      throw error;
    }
  };

  const editButtonClicked = () => {
    if (getSurveyResponse?.survey.status === "published") {
      setIsEditModeDialogOpen(true);
    } else {
      navigate(`./edit/existing`);
    }
  };

  const handleEditMode = (value: boolean) => {
    setIsEditModeDialogOpen(false);
    if (value) {
      navigate(`./edit/existing`);
    } else {
      navigate(`./edit`);
    }
  };

  const handleFormTypeChange = (
    event: React.MouseEvent<HTMLElement>,
    newValue: boolean | null,
  ) => {
    if (newValue !== null) {
      setIsCondensedForm(newValue);
      setCurrentEditJSON(newValue ? condensedSurveyJSON : mainSurveyJSON);
    }
  };

  const handleOpenGenerateMenu = (event: React.MouseEvent<HTMLElement>) => {
    setGenerateMenuAnchor(event.currentTarget);
  };

  const handleCloseGenerateMenu = () => {
    setGenerateMenuAnchor(null);
  };

  const handleGenerateCondensedReport = () => {
    handleCloseGenerateMenu();
    const hasExistingCondensed =
      condensedSurveyJSON && Object.keys(condensedSurveyJSON).length > 0;

    if (hasExistingCondensed) {
      setIsConfirmCondensedModalOpen(true);
    } else {
      generateCondensedReport();
    }
  };

  const generateCondensedReport = async () => {
    if (!orgId || !surveyId) {
      toast.error(
        "Organization ID and Survey ID are required to generate a condensed report.",
      );
      return;
    }
    try {
      const response = await postSurveyCondensed({
        path: {
          org_id: orgId,
          survey_id: surveyId,
        },
      });

      if (response.data?.success) {
        setCondensedSurveyJSON(response.data.pdf_configuration);
        if (isCondensedForm) {
          setCurrentEditJSON(response.data.pdf_configuration);
        }
        toast.success("Condensed report generated successfully!");
      }
    } catch (error) {
      console.error("Error generating condensed report:", error);
      toast.error("Failed to generate condensed report. Please try again.");
    }
  };

  const handleConfirmGenerate = () => {
    setIsConfirmCondensedModalOpen(false);
    generateCondensedReport();
  };

  const handleDeploySuccess = async () => {
    if (surveyId && orgId) {
      const response = await getSurvey({ path: { survey_id: surveyId } });
      setGetSurveyResponse(response.data!);
      setMainSurveyJSON(response.data!.survey.configuration);
      setCondensedSurveyJSON(response.data!.survey.pdf_configuration);
      setCurrentEditJSON(response.data!.survey.configuration);
      setDraftVersion(null);

      try {
        const versionsResponse = await listSurveyVersions({
          path: { org_id: orgId, parent_survey_id: surveyId },
        });

        const activeVersionData = versionsResponse.data?.versions?.find(
          (v) => v.status === "active",
        );
        if (activeVersionData) {
          setActiveVersion(activeVersionData);
        }
      } catch (error) {
        console.error("Error loading updated active version:", error);
      }

      if (isPreDeployComparison) {
        handleExitComparison();
      }

      // Navigate to view mode to see the newly deployed active version
      navigate(`/${app}/${orgId}/forms/surveys/${surveyId}`, {
        replace: true,
      });
    }
  };

  const handleDeleteDraft = async () => {
    if (!surveyId || !orgId) {
      toast.error("Cannot delete draft: Missing survey or organization ID");
      return;
    }

    setIsDeletingDraft(true);
    try {
      await deleteSurveyVersionDraft({
        path: { org_id: orgId, parent_survey_id: surveyId },
      });

      toast.success("Draft deleted successfully");
      setIsDeleteDraftModalOpen(false);
      setDraftVersion(null);

      // Reload the survey to show the active version
      if (activeVersion) {
        setMainSurveyJSON(activeVersion.configuration);
        setCondensedSurveyJSON(activeVersion.pdf_configuration);
        setCurrentEditJSON(activeVersion.configuration);
        // Navigate to view mode since there's no draft to edit
        navigate(`/${app}/${orgId}/forms/surveys/${surveyId}`, {
          replace: true,
        });
      } else {
        // No active version either - navigate back to survey list
        navigate(`/${app}/${orgId}/forms/surveys`, { replace: true });
      }
    } catch (error) {
      console.error("Error deleting draft:", error);
      toast.error("Failed to delete draft");
    } finally {
      setIsDeletingDraft(false);
    }
  };

  const handleCompare = async (
    fromVersion: SurveyVersionModel,
    toVersion: SurveyVersionModel,
  ) => {
    setIsVersionHistoryModalOpen(false);

    const fromId = fromVersion.status === "draft" ? "draft" : fromVersion.id;
    const toId = toVersion.status === "draft" ? "draft" : toVersion.id;
    setSearchParams({ compare: `${fromId}~${toId}` });
  };

  const handleExitComparison = () => {
    setSearchParams({});
  };

  const handleVersionSelect = async (version: SurveyVersionModel) => {
    setIsVersionHistoryModalOpen(false);

    if (version.status === "draft") {
      setSearchParams({});
      navigate(`/${app}/${orgId}/forms/surveys/${surveyId}/edit`);
    } else {
      setSearchParams({ view: version.id });
    }
  };

  const handleDeployClick = async () => {
    if (!draftVersion || !activeVersion || !surveyId || !orgId) {
      setIsDeployModalOpen(true);
      return;
    }

    setCompareFromVersion(activeVersion);
    setCompareToVersion(draftVersion);
    setComparisonMode(true);
    setIsPreDeployComparison(true);
    setSelectedVersionView("compare");
    setSearchParams({ compare: `${activeVersion.id}~draft` });
  };

  const handleConfirmDeploy = () => {
    setIsDeployModalOpen(true);
  };

  const isUnchangedDraft = useMemo(() => {
    if (!draftVersion || !activeVersion) return false;

    const configsMatch =
      JSON.stringify(draftVersion.configuration) ===
      JSON.stringify(activeVersion.configuration);
    const pdfConfigsMatch =
      JSON.stringify(draftVersion.pdf_configuration) ===
      JSON.stringify(activeVersion.pdf_configuration);

    return configsMatch && pdfConfigsMatch;
  }, [draftVersion, activeVersion]);

  const handleVersionViewChange = (event: any) => {
    setSelectedVersionView(event.target.value);
  };

  const handleCopySurveyId = () => {
    if (surveyId) {
      navigator.clipboard.writeText(surveyId);
      toast.success("Survey ID copied to clipboard");
    }
  };

  const handleBackNavigation = () => {
    if (
      comparisonMode ||
      searchParams.get("view") ||
      searchParams.get("compare")
    ) {
      window.history.back();
      return;
    }

    if (mode === "edit") {
      navigate(`/${app}/${orgId}/forms/surveys/${surveyId}`);
    } else {
      navigate(`/${app}/${orgId}/forms/surveys`);
    }
  };

  const displayedSurveyJSON = useMemo(() => {
    if (!comparisonMode) {
      return currentEditJSON;
    }

    if (selectedVersionView === "fromVersion" && compareFromVersion) {
      return isCondensedForm
        ? compareFromVersion.pdf_configuration
        : compareFromVersion.configuration;
    }

    if (selectedVersionView === "toVersion" && compareToVersion) {
      return isCondensedForm
        ? compareToVersion.pdf_configuration
        : compareToVersion.configuration;
    }

    return currentEditJSON;
  }, [
    comparisonMode,
    selectedVersionView,
    compareFromVersion,
    compareToVersion,
    isCondensedForm,
    currentEditJSON,
  ]);

  const FormBuilderMemo = useMemo(() => {
    const effectiveMode =
      comparisonMode && selectedVersionView !== "compare" ? "view" : mode;

    return (
      <FormBuilder
        onSave={handleSave}
        mode={effectiveMode}
        surveyJSObject={displayedSurveyJSON}
        isCondensedForm={isCondensedForm}
        supportedLanguages={supportedLanguages}
      />
    );
  }, [
    displayedSurveyJSON,
    isCondensedForm,
    mode,
    comparisonMode,
    selectedVersionView,
    supportedLanguages,
    handleSave,
  ]);

  if (isLoading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        height="100vh"
        gap="16px"
      >
        <CircularProgress size={48} />
        <Typography variant="body2" color="text.secondary">
          Loading survey...
        </Typography>
      </Box>
    );
  }

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Box
        display="flex"
        flexDirection="column"
        gap="16px"
        flex={1}
        minHeight={0}
      >
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="space-between"
          boxShadow="0px 2px 4px rgba(0, 0, 0, 0.1)"
          borderRadius="8px"
          marginLeft="16px"
          marginRight="16px"
          padding="16px"
        >
          <Box display="flex" alignItems="center" gap="16px">
            {/* Left Section - Navigation and toggles */}
            <Box
              display="flex"
              alignItems="center"
              gap="16px"
              flex={1}
              justifyContent="flex-start"
              sx={{ minWidth: 0 }}
            >
              <Button
                variant="outlined"
                color="primary"
                size="small"
                startIcon={<ArrowBack />}
                onClick={handleBackNavigation}
              >
                {isPreDeployComparison
                  ? "Back"
                  : mode === "edit"
                    ? "View"
                    : "Back"}
              </Button>
              <ToggleButtonGroup
                color="primary"
                value={isCondensedForm}
                exclusive
                onChange={handleFormTypeChange}
                aria-label="Form Type"
                size="small"
              >
                <ToggleButton
                  value={false}
                  sx={{
                    minWidth: 90,
                    "&.Mui-selected": {
                      bgcolor: "primary.main",
                      color: colors.common.white,
                      "&:hover": {
                        bgcolor: "primary.dark",
                      },
                    },
                  }}
                >
                  Main
                </ToggleButton>
                <ToggleButton
                  value={true}
                  sx={{
                    minWidth: 90,
                    "&.Mui-selected": {
                      bgcolor: "primary.main",
                      color: colors.common.white,
                      "&:hover": {
                        bgcolor: "primary.dark",
                      },
                    },
                  }}
                >
                  Condensed
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* Center Section - Title */}
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              flex={1}
              sx={{ minWidth: 0, overflow: "hidden" }}
            >
              <Typography
                variant="body1"
                color="text.primary"
                sx={{
                  whiteSpace: "nowrap",
                  fontWeight: 500,
                }}
              >
                {isPreDeployComparison
                  ? "Reviewing deployment"
                  : comparisonMode && compareToVersion
                    ? "Viewing version comparison"
                    : comparisonMode && compareFromVersion
                      ? `Viewing ${compareFromVersion.status === "active" ? "Active" : "Archived"} v${formatVersionNumber(compareFromVersion)}`
                      : mode === "view"
                        ? activeVersion
                          ? `Viewing Active v${formatVersionNumber(activeVersion)}`
                          : "No active version deployed"
                        : draftVersion
                          ? "Editing draft"
                          : "Editing survey"}
              </Typography>

              {/* ID Display Section - Horizontal layout */}
              <Box
                display="flex"
                flexDirection="row"
                alignItems="center"
                gap="8px"
                sx={{
                  mt: 0.25,
                  minWidth: 0,
                  overflow: "hidden",
                  flexShrink: 1,
                }}
              >
                {/* Parent Survey ID */}
                <Box
                  display="flex"
                  alignItems="center"
                  gap="4px"
                  sx={{
                    cursor: "pointer",
                    userSelect: "none",
                    "&:hover": { opacity: 0.7 },
                    minWidth: 0,
                  }}
                  onClick={handleCopySurveyId}
                  title="Click to copy Parent Survey ID"
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: "0.7rem", flexShrink: 0 }}
                  >
                    Parent:
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      fontSize: "0.7rem",
                      fontFamily: "monospace",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {surveyId}
                  </Typography>
                </Box>

                {/* Separator */}
                {((mode === "view" && activeVersion) ||
                  (mode === "edit" && draftVersion)) && (
                  <Typography variant="caption" color="text.secondary">
                    •
                  </Typography>
                )}

                {/* Version ID (only show when we have a version) */}
                {(mode === "view" && activeVersion) ||
                (mode === "edit" && draftVersion) ? (
                  <Box
                    display="flex"
                    alignItems="center"
                    gap="4px"
                    sx={{
                      cursor: "pointer",
                      userSelect: "none",
                      "&:hover": { opacity: 0.7 },
                      minWidth: 0,
                    }}
                    onClick={() => {
                      const versionId =
                        mode === "view" ? activeVersion?.id : draftVersion?.id;
                      if (versionId) {
                        navigator.clipboard.writeText(versionId);
                        toast.success("Version ID copied to clipboard");
                      }
                    }}
                    title="Click to copy Version ID"
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: "0.7rem", flexShrink: 0 }}
                    >
                      {mode === "view" ? "Viewing:" : "Editing:"}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        fontSize: "0.7rem",
                        fontFamily: "monospace",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {mode === "view" ? activeVersion?.id : draftVersion?.id}
                    </Typography>
                  </Box>
                ) : null}
              </Box>
            </Box>

            {/* Right Section - Action buttons */}
            <Box
              display="flex"
              alignItems="center"
              gap="16px"
              flex={1}
              justifyContent="flex-end"
              sx={{ minWidth: 0 }}
            >
              {comparisonMode ? (
                <>
                  {/* Version comparison dropdown */}
                  {compareFromVersion && compareToVersion && (
                    <FormControl
                      size="small"
                      variant="outlined"
                      sx={{ minWidth: 120, flexShrink: 0 }}
                    >
                      <Select
                        value={selectedVersionView}
                        onChange={handleVersionViewChange}
                        displayEmpty
                      >
                        <MenuItem value="fromVersion">
                          {compareFromVersion.status === "draft"
                            ? "Preview Draft"
                            : `Preview v${formatVersionNumber(compareFromVersion)}`}
                        </MenuItem>
                        <MenuItem value="toVersion">
                          {compareToVersion.status === "draft"
                            ? "Preview Draft"
                            : `Preview v${formatVersionNumber(compareToVersion)}`}
                        </MenuItem>
                        <MenuItem value="compare">Compare JSON</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                  {isPreDeployComparison ? (
                    <>
                      <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        onClick={handleExitComparison}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        startIcon={<Publish />}
                        onClick={handleConfirmDeploy}
                      >
                        Confirm Deploy
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      onClick={handleExitComparison}
                    >
                      {compareToVersion ? "Exit Comparison" : "Return to Draft"}
                    </Button>
                  )}
                </>
              ) : (
                <>
                  {/* Lint status summary - only in edit mode, hidden in demo mode */}
                  {mode === "edit" && lintResult && !isLintHidden && (
                    <LintSummary
                      result={lintResult}
                      onClick={() =>
                        setIsLintPanelExpanded(!isLintPanelExpanded)
                      }
                    />
                  )}

                  {/* Tools button - only in edit mode */}
                  {mode === "edit" && (
                    <>
                      <Button
                        variant="outlined"
                        color="secondary"
                        size="small"
                        startIcon={<BuildIcon />}
                        endIcon={<ArrowDropDown />}
                        onClick={handleOpenGenerateMenu}
                      >
                        Tools
                      </Button>
                      <Menu
                        anchorEl={generateMenuAnchor}
                        open={Boolean(generateMenuAnchor)}
                        onClose={handleCloseGenerateMenu}
                      >
                        <MenuItem onClick={handleGenerateCondensedReport}>
                          Generate Condensed Report
                        </MenuItem>
                        <MenuItem onClick={() => setIsLintHidden((v) => !v)}>
                          <ListItemIcon>
                            <Checkbox
                              edge="start"
                              checked={isLintHidden}
                              tabIndex={-1}
                              disableRipple
                              sx={{ p: 0, mr: 1 }}
                            />
                          </ListItemIcon>
                          Hide lint feedback
                        </MenuItem>
                        {draftVersion && (
                          <>
                            <Divider />
                            <MenuItem
                              onClick={() => {
                                handleCloseGenerateMenu();
                                setIsDeleteDraftModalOpen(true);
                              }}
                              sx={{ color: "error.main" }}
                            >
                              Delete Draft
                            </MenuItem>
                          </>
                        )}
                      </Menu>
                    </>
                  )}

                  {/* History button always available */}
                  <Button
                    variant="outlined"
                    color="secondary"
                    size="small"
                    startIcon={<HistoryIcon />}
                    onClick={() => setIsVersionHistoryModalOpen(true)}
                  >
                    History
                  </Button>

                  {(mode === "edit" && draftVersion) || mode === "view" ? (
                    <>
                      <Divider
                        orientation="vertical"
                        flexItem
                        sx={{ mx: 1, height: "24px", alignSelf: "center" }}
                      />

                      {mode === "edit" && draftVersion ? (
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          startIcon={<Publish />}
                          onClick={handleDeployClick}
                        >
                          Deploy
                        </Button>
                      ) : mode === "view" ? (
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          startIcon={<Edit />}
                          onClick={editButtonClicked}
                        >
                          Edit
                        </Button>
                      ) : null}
                    </>
                  ) : null}
                </>
              )}
            </Box>
          </Box>
        </Box>

        {/* Unchanged draft warning for deploy preview */}
        {isPreDeployComparison && isUnchangedDraft && (
          <Box marginLeft="16px" marginRight="16px">
            <Alert severity="warning">
              No changes detected between draft and active version
            </Alert>
          </Box>
        )}

        {/* Draft-only warning banner */}
        {draftVersion && !activeVersion && (
          <Box marginLeft="16px" marginRight="16px">
            <Alert severity="info">
              <strong>Draft Only</strong> - This survey has not been deployed
              yet.{" "}
              {mode === "edit"
                ? 'Click the "Deploy" button to make it active and available for use.'
                : "Switch to edit mode to deploy this survey and make it active."}
            </Alert>
          </Box>
        )}

        <Box
          marginLeft="16px"
          marginRight="16px"
          display="flex"
          flexDirection="column"
          flex={1}
          minHeight={0}
          gap="8px"
        >
          <Box flex={1} minHeight={0}>
            {comparisonMode &&
            compareFromVersion &&
            compareToVersion &&
            surveyId &&
            orgId ? (
              <Box height="100%">
                {selectedVersionView === "compare" ? (
                  <VersionComparison
                    fromVersion={compareFromVersion}
                    toVersion={compareToVersion}
                    viewType={
                      isCondensedForm ? "pdf_configuration" : "configuration"
                    }
                  />
                ) : (
                  FormBuilderMemo
                )}
              </Box>
            ) : (
              FormBuilderMemo
            )}
          </Box>

          {/* Lint Panel - only show in edit mode with results, hidden in demo mode */}
          {mode === "edit" &&
            lintResult &&
            !comparisonMode &&
            !isLintHidden && (
              <Box flexShrink={0} mb={2}>
                <LintPanel
                  result={lintResult}
                  defaultExpanded={isLintPanelExpanded}
                  maxHeight={200}
                />
              </Box>
            )}
        </Box>
      </Box>

      {/* Modals */}
      <EditModeModal
        isEditModeDialogOpen={isEditModeDialogOpen}
        setIsEditModeDialogOpen={setIsEditModeDialogOpen}
        onEditModeSelect={handleEditMode}
      />
      <ConfirmCondensedModal
        isOpen={isConfirmCondensedModalOpen}
        onClose={() => setIsConfirmCondensedModalOpen(false)}
        onConfirm={handleConfirmGenerate}
      />

      {/* Version control modals */}
      {surveyId && orgId && (
        <>
          <DeployModal
            open={isDeployModalOpen}
            onClose={() => {
              setIsDeployModalOpen(false);
              if (isPreDeployComparison) {
                handleExitComparison();
              }
            }}
            parentSurveyId={surveyId}
            orgId={orgId}
            draftVersion={draftVersion}
            activeVersion={activeVersion}
            onDeploySuccess={handleDeploySuccess}
            lintResult={lintResult}
          />
          <VersionHistoryModal
            open={isVersionHistoryModalOpen}
            onClose={() => setIsVersionHistoryModalOpen(false)}
            parentSurveyId={surveyId}
            orgId={orgId}
            onCompare={handleCompare}
            onVersionSelect={handleVersionSelect}
          />
        </>
      )}

      {/* Delete Draft Confirmation Modal */}
      <Dialog
        open={isDeleteDraftModalOpen}
        onClose={() => !isDeletingDraft && setIsDeleteDraftModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Draft?</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone.
          </Alert>
          <Typography>
            Are you sure you want to delete this draft? All unsaved changes will
            be permanently lost.
          </Typography>
          {activeVersion && (
            <Typography sx={{ mt: 2 }} color="text.secondary">
              The survey will revert to showing the active version (v
              {activeVersion.major_version}.{activeVersion.minor_version}).
            </Typography>
          )}
          {!activeVersion && (
            <Typography sx={{ mt: 2 }} color="text.secondary">
              Since there is no active version, you will be returned to the
              survey list.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setIsDeleteDraftModalOpen(false)}
            disabled={isDeletingDraft}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteDraft}
            color="error"
            variant="contained"
            disabled={isDeletingDraft}
            startIcon={
              isDeletingDraft ? <CircularProgress size={16} /> : undefined
            }
          >
            {isDeletingDraft ? "Deleting..." : "Delete Draft"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
